import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import {
  ArrowRight,
  MapPin,
  Megaphone,
  ClipboardText,
  Sparkle,
  Wrench,
  Leaf,
} from "@phosphor-icons/react";
import { Header, MobileNav } from "@/components/Header";
import StatusBadge from "@/components/StatusBadge";
import api from "@/lib/api";
import { useBuilding } from "@/context/BuildingContext";
import { categoryByKey, formatDate, formatDateShort } from "@/lib/constants";

const HERO_IMG =
  "https://images.unsplash.com/photo-1614595737476-42487331b8a1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjd8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjByZXNpZGVudGlhbCUyMGJ1aWxkaW5nJTIwYXJjaGl0ZWN0dXJlfGVufDB8fHx8MTc5MTcyNDY3Mnww&ixlib=rb-4.1.0&q=85";

const ACTIONS = [
  {
    to: "/signaler",
    Icon: Megaphone,
    title: "Signaler un problème",
    desc: "Ampoule grillée, fuite, dégradation… Prévenez le gestionnaire en deux minutes, sans compte.",
    testid: "action-card-signaler",
  },
  {
    to: "/signalements",
    Icon: ClipboardText,
    title: "Consulter les signalements",
    desc: "Suivez les demandes de la résidence et leur résolution, pas à pas.",
    testid: "action-card-signalements",
  },
  {
    to: "/entretien",
    Icon: Sparkle,
    title: "Suivi entretien",
    desc: "Confirmez un passage de nettoyage et consultez l'historique complet.",
    testid: "action-card-entretien",
  },
  {
    to: "/services",
    Icon: Wrench,
    title: "Services aux résidents",
    desc: "Des artisans de confiance pour votre logement, recommandés par la résidence.",
    testid: "action-card-services",
  },
];

function Reveal({ children, delay = 0 }) {
  return (
    <span className="block overflow-hidden pb-1">
      <motion.span
        className="block"
        initial={{ y: "110%" }}
        animate={{ y: 0 }}
        transition={{ duration: 0.9, delay, ease: [0.22, 1, 0.36, 1] }}
      >
        {children}
      </motion.span>
    </span>
  );
}

function ActionCard({ to, Icon, title, desc, delay, testid }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 28 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.55, delay, ease: "easeOut" }}
    >
      <Link to={to} data-testid={testid} className="block h-full">
        <motion.div
          whileHover={{ y: -6 }}
          whileTap={{ scale: 0.98 }}
          className="group flex h-full flex-col rounded-3xl border border-black/5 bg-white p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)] transition-shadow duration-300 hover:shadow-[0_18px_44px_rgb(0,0,0,0.09)]"
        >
          <span className="mb-5 flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/10 text-primary transition-colors duration-300 group-hover:bg-primary group-hover:text-primary-foreground">
            <Icon size={24} weight="duotone" />
          </span>
          <h3 className="font-display text-lg font-bold tracking-tight">{title}</h3>
          <p className="mt-2 flex-1 text-sm leading-relaxed text-muted-foreground">{desc}</p>
          <span className="mt-5 inline-flex items-center gap-2 text-sm font-bold text-primary">
            Ouvrir
            <ArrowRight size={16} weight="bold" className="transition-transform duration-300 group-hover:translate-x-1" />
          </span>
        </motion.div>
      </Link>
    </motion.div>
  );
}

export default function Home() {
  const BUILDING = useBuilding();
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 70]);
  const [latest, setLatest] = useState([]);

  useEffect(() => {
    api.get("/reports").then((r) => setLatest(r.data.slice(0, 3))).catch(() => {});
  }, []);

  const lastReport = latest[0] || null;
  const { Icon: LastReportIcon, label: lastReportLabel } = lastReport ? categoryByKey(lastReport.category) : {};

  return (
    <div className="pb-24 md:pb-0" data-testid="home-page">
      <Header />
      <main>
        <section ref={heroRef} className="noise relative overflow-hidden">
          <div className="mx-auto grid max-w-5xl items-center gap-12 px-5 pb-20 pt-14 sm:pt-20 md:grid-cols-[1.05fr_0.95fr]">
            <div>
              <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="mb-6 inline-block rounded-2xl border border-black/5 bg-white px-4 py-2.5 text-xs shadow-sm"
                data-testid="hero-address-chip"
              >
                <span className="flex items-center gap-2 font-bold text-primary">
                  <MapPin size={14} weight="fill" />
                  {BUILDING.name}
                </span>
                {(BUILDING.addresses || [BUILDING.address]).filter(Boolean).map((a, i) => (
                  <span key={i} className="mt-0.5 block pl-[22px] font-medium text-muted-foreground">{a}</span>
                ))}
              </motion.div>
              <h1 className="font-display text-5xl font-extrabold leading-[1.04] tracking-[-0.02em] sm:text-6xl">
                <Reveal delay={0.1}>Votre copropriété,</Reveal>
                <Reveal delay={0.22}>entre de</Reveal>
                <Reveal delay={0.34}>
                  <span className="text-[#265442]">bonnes mains.</span>
                </Reveal>
              </h1>
              <motion.p
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.55 }}
                className="mt-6 max-w-md text-base leading-relaxed text-muted-foreground"
              >
                Signalez un souci en quelques secondes, suivez sa résolution et confirmez les
                passages d'entretien. Simple, transparent, sans compte à créer.
              </motion.p>
              <motion.div
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.7 }}
                className="mt-8 flex flex-wrap items-center gap-3"
              >
                <Link
                  to="/signaler"
                  data-testid="hero-cta-signaler"
                  className="group inline-flex items-center gap-2 rounded-full bg-primary px-7 py-3.5 text-sm font-bold text-primary-foreground shadow-[0_10px_28px_rgba(24,56,43,0.28)] transition-colors duration-300 hover:bg-[#265442]"
                >
                  Signaler un problème
                  <ArrowRight size={16} weight="bold" className="transition-transform duration-300 group-hover:translate-x-1" />
                </Link>
                <Link
                  to="/signalements"
                  data-testid="hero-cta-consulter"
                  className="rounded-full border border-black/10 bg-white px-7 py-3.5 text-sm font-bold text-foreground transition-colors duration-300 hover:border-primary hover:text-primary"
                >
                  Voir les signalements
                </Link>
              </motion.div>
            </div>
            <motion.div
              style={{ y: imgY }}
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.9, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
              className="relative"
            >
              <div className="overflow-hidden rounded-[2rem] border border-black/5 shadow-[0_24px_60px_rgba(24,56,43,0.18)]">
                <img
                  src={HERO_IMG}
                  alt="Façade de la résidence"
                  className="aspect-[4/5] w-full object-cover sm:aspect-[5/6]"
                />
              </div>
              {lastReport && (
                <motion.div
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.7, delay: 1 }}
                  className="absolute -bottom-5 left-5"
                >
                  <Link
                    to={`/suivi/${lastReport.ref}`}
                    className="flex items-center gap-3 rounded-2xl border border-black/5 bg-white/85 px-4 py-3 shadow-lg backdrop-blur-xl"
                    data-testid="hero-last-report-card"
                  >
                    <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary/10 text-primary">
                      <LastReportIcon size={18} weight="duotone" />
                    </span>
                    <span className="text-xs font-semibold leading-tight">
                      Dernier signalement
                      <span className="block text-muted-foreground">
                        {lastReportLabel} · {formatDateShort(lastReport.created_at)}
                      </span>
                    </span>
                  </Link>
                </motion.div>
              )}
            </motion.div>
          </div>
        </section>

        <div className="overflow-hidden border-y border-black/5 bg-primary py-3.5" data-testid="marquee-strip">
          <div className="animate-marquee flex w-max items-center gap-8">
            {[0, 1].map((n) => (
              <div key={n} className="flex items-center gap-8">
                {["Propreté", "Transparence", "Réactivité", "Proximité", "Confiance", "Entretien"].map((w) => (
                  <span key={w} className="flex items-center gap-8 font-display text-sm font-bold uppercase tracking-[0.25em] text-primary-foreground/80">
                    {w}
                    <Leaf size={14} weight="fill" className="text-[#7FC8A9]" />
                  </span>
                ))}
              </div>
            ))}
          </div>
        </div>

        <section className="mx-auto max-w-5xl px-5 py-16 sm:py-24">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="font-display text-4xl font-extrabold tracking-[-0.02em] sm:text-5xl"
          >
            Que souhaitez-vous faire ?
          </motion.h2>
          <div className="mt-10 grid gap-6 md:grid-cols-2">
            {ACTIONS.filter((a) => a.to !== "/entretien" || BUILDING.features?.cleaning !== false).map((a, i) => (
              <ActionCard key={a.to} {...a} delay={i * 0.08} />
            ))}
          </div>
        </section>

        {latest.length > 0 && (
          <section className="mx-auto max-w-5xl px-5 pb-20">
            <div className="mb-6 flex items-end justify-between">
              <h2 className="font-display text-2xl font-bold tracking-tight">Derniers signalements</h2>
              <Link to="/signalements" className="text-sm font-bold text-primary hover:underline" data-testid="home-see-all-reports">
                Tout voir
              </Link>
            </div>
            <div className="grid gap-4 sm:grid-cols-3">
              {latest.map((r, i) => {
                const { Icon, label } = categoryByKey(r.category);
                return (
                  <motion.div
                    key={r.id}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: i * 0.08 }}
                  >
                    <Link
                      to={`/suivi/${r.ref}`}
                      data-testid={`home-report-card-${r.ref}`}
                      className="block h-full rounded-3xl border border-black/5 bg-white p-5 shadow-[0_8px_30px_rgb(0,0,0,0.04)] transition-shadow duration-300 hover:shadow-[0_14px_36px_rgb(0,0,0,0.08)]"
                    >
                      <div className="mb-3 flex items-center justify-between">
                        <Icon size={22} weight="duotone" className="text-primary" />
                        <StatusBadge status={r.status} />
                      </div>
                      <p className="font-display text-sm font-bold">{label}</p>
                      <p className="mt-1 text-xs text-muted-foreground">
                        {r.location} · {formatDate(r.created_at)}
                      </p>
                    </Link>
                  </motion.div>
                );
              })}
            </div>
          </section>
        )}

        <footer className="border-t border-black/5 bg-white">
          <div className="mx-auto flex max-w-5xl flex-col gap-4 px-5 py-10 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="font-display text-base font-bold">Ma Copropriété</p>
              {(BUILDING.addresses || [BUILDING.address]).filter(Boolean).map((a, i) => (
                <p key={i} className="mt-1 text-xs text-muted-foreground">{a}</p>
              ))}
            </div>
            <Link to="/admin" className="text-xs font-semibold text-muted-foreground transition-colors hover:text-primary" data-testid="footer-admin-link">
              Espace gestionnaire
            </Link>
          </div>
        </footer>
      </main>
      <MobileNav />
    </div>
  );
}
